import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs.tsx";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area.tsx";
import { ReadOnlyTweetCard } from "@/views/manage_personas/ReadOnlyTweetCard.tsx";
import { LoaderCircle, User } from "lucide-react";
import { apiUrl } from "@/components/automate/InteractionLog.tsx";
import { useLatestGeneratedContentQuery } from "@/api/twitter.ts";
import { Persona } from "@/api/personas.ts";
import { useAuth } from "@/hooks/use-auth";
import {
  type ContentToReview,
  useReviewContentQuery,
} from "@/api/content-review.ts";
import { useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Link } from "react-router-dom";
import { agentQueries, uploadImage } from "@/api/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";

export function ContentTabContent({ persona }: { persona: Persona }) {
  const { authenticated } = useAuth();
  const [selectedTab, setSelectedTab] = useState("tweets");
  const queryClient = useQueryClient();
  const {
    isFetching: isContentLoading,
    isSuccess: isContentSuccess,
    data: fetchedTweets,
  } = useLatestGeneratedContentQuery(persona.persona_id, authenticated);
  const { data: previousContent } = useReviewContentQuery(
    { persona: persona.persona_id, order_by: "timestamp" },
    authenticated,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewItem, setPreviewItem] = useState<ContentToReview | null>(null);
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        await uploadImage(file, persona.persona_id);
        queryClient.invalidateQueries({
          queryKey: ["review-content", { persona: persona.persona_id }],
        });
      } catch (error) {
        console.error("Error uploading image:", error);
      }
    }
  };
  const { data } = useQuery(agentQueries.agent(persona.persona_id));
  const canEdit = data?.can_edit_persona;

  return (
    <>
      <Tabs defaultValue="tweets" onValueChange={setSelectedTab}>
        <div className="flex flex-row justify-between items-center flex-wrap-reverse gap-y-4">
          <ScrollArea>
            <TabsList className="mb-4 gap-4 bg-transparent [&>*]:!rounded-lg self-start w-full justify-start">
              <TabsTrigger
                className="text-white bg-transparent active:text-primary data-[state=active]:border-primary border"
                value="tweets"
              >
                Tweets
              </TabsTrigger>
              <TabsTrigger
                className="text-white bg-transparent active:text-primary data-[state=active]:border-primary border"
                value="visuals"
              >
                Visuals
              </TabsTrigger>
            </TabsList>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>

          <div className="flex flex-row gap-2">
            {selectedTab === "visuals" && canEdit && (
              <Button onClick={() => fileInputRef.current?.click()}>
                UPLOAD IMAGE
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  className="hidden"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                />
              </Button>
            )}
            {canEdit && (
              <Button asChild>
                <Link to="/create">Create new content</Link>
              </Button>
            )}
          </div>
        </div>

        <TabsContent value="tweets">
          {isContentSuccess && fetchedTweets?.pages?.[0]?.data?.length > 0 ? (
            <div className="tweet-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
              {fetchedTweets?.pages?.[0]?.data?.map((tweet) => (
                <ReadOnlyTweetCard
                  key={tweet.doc_id}
                  tweet={tweet}
                  personaData={persona}
                />
              ))}
            </div>
          ) : isContentLoading ? (
            <LoaderCircle className="h-12 w-12 text-center animate-spin stroke-accent m-auto" />
          ) : (
            <p className="text-center">No tweets found</p>
          )}
        </TabsContent>
        <TabsContent value="visuals">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
            {(previousContent?.pages?.[0]?.data?.length ?? 0) > 0 ? (
              previousContent?.pages?.[0]?.data.map((item) => {
                return (
                  <div
                    key={item.doc_id}
                    className="relative max-w-[270px] aspect-[3/2] rounded-2xl border border-white/10"
                  >
                    <img
                      src={`${apiUrl}/get-file?url=${encodeURIComponent(
                        item.thumbnail_url,
                      )}`}
                      alt={item.image_caption}
                      data-testid={`image-${item.thumbnail_url}`}
                      className={`w-full h-full ${item.origin === "uploaded" ? "object-contain" : "object-cover"} rounded-2xl transition-transform duration-200 hover:scale-105 cursor-pointer`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setPreviewItem(item);
                      }}
                    />
                    {item.thumbnail_url?.includes("uploaded") && (
                      <User className="absolute top-2 right-2 w-4 h-4 text-black pointer-events-none rounded-full ring-2 ring-white bg-white/70 backdrop-blur-sm" />
                    )}
                  </div>
                );
              })
            ) : (
              <p className="text-center">No content generated</p>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <ImagePreviewDialog
        previewItem={previewItem}
        setPreviewItem={setPreviewItem}
      />
    </>
  );
}

function ImagePreviewDialog({
  previewItem,
  setPreviewItem,
}: {
  previewItem: ContentToReview | null;
  setPreviewItem: (item: ContentToReview | null) => void;
}) {
  return (
    <Dialog
      open={previewItem !== null}
      onOpenChange={() => setPreviewItem(null)}
    >
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Image Preview</DialogTitle>
        </DialogHeader>
        {previewItem && (
          <div className="relative">
            <img
              src={`${apiUrl}/get-file?url=${encodeURIComponent(previewItem.thumbnail_url)}`}
              alt={previewItem.image_caption}
              className="w-full h-auto object-cover"
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
